import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";

import VariantEditor from "../../containers/MessageTemplateEditor/VariantEditor";
import TemplateHeader from "../../containers/MessageTemplateEditor/TemplateHeader";
import AddLanguageDialog from "../../containers/MessageTemplateEditor/AddLanguageDialog";
import { PATHS } from "../../constants/paths";
import { keepState, resolveReturnTo } from "../../utils/returnTo";
import {
  messageTemplateApi,
  pickDefaultLocale,
} from "../../utils/messageTemplateApi";

const useStyles = makeStyles({
  page: {
    backgroundColor: "#f7f9fc",
    minHeight: "calc(100vh - 64px)",
    paddingTop: "16px",
    paddingBottom: "32px",
  },
  back: { minHeight: "44px", marginBottom: "8px" },
  title: { fontSize: "24px", fontWeight: 700, color: "#101828" },
  panel: {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginBottom: "16px",
  },
  row: { display: "flex", gap: "12px", flexWrap: "wrap" },
  grow: { flex: "1 1 260px" },
  languageBar: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "16px",
    borderBottom: "1px solid #d0d5dd",
  },
  tabs: { minHeight: "44px", flex: "1 1 auto", minWidth: 0 },
  tab: { minHeight: "44px", textTransform: "none", minWidth: "88px" },
  deletedTab: { textDecoration: "line-through", color: "#667085" },
  touch: { minHeight: "44px" },
  progressWrap: {
    display: "flex",
    justifyContent: "center",
    padding: "48px 0",
  },
});

const templateUrl = (id, localeId, params) =>
  `${PATHS.MESSAGE_TEMPLATES}/${id}${
    localeId ? `/${encodeURIComponent(localeId)}` : ""
  }${params ? `?${new URLSearchParams(params)}` : ""}`;

const sameLocale = (a, b) =>
  (a || "").toLowerCase() === (b || "").toLowerCase();

const confirmDiscard = (dirty) =>
  // eslint-disable-next-line no-alert
  !dirty || window.confirm("Discard unsaved changes?");

/**
 * `/message-templates/new` creates a template with its first language.
 * `/message-templates/:id/:localeId` edits one language of a template; without a locale it opens
 * the admin's own language (or en-US). A locale the template does not have yet opens the
 * add-language editor, prefilled from `?from=<locale>`.
 */
const MessageTemplateEdit = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { id, localeId: routeLocale } = useParams();
  const isNew = id === "new";
  const numericId = isNew ? null : Number(id);
  // Moves within the editor keep the way back to the filtered list.
  const go = (to, options) =>
    navigate(to, { ...options, state: keepState(location) });
  const search = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const [definitions, setDefinitions] = useState([]);
  const [locales, setLocales] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);
  const [startContent, setStartContent] = useState(null);

  // create form
  const [templateName, setTemplateName] = useState(
    () => search.get("templateName") || "",
  );
  const [description, setDescription] = useState("");
  const [newLocale, setNewLocale] = useState("");
  const [startFrom, setStartFrom] = useState("");

  useEffect(() => {
    Promise.all([
      messageTemplateApi.definitions(),
      messageTemplateApi.locales(),
    ])
      .then(([defs, locs]) => {
        setDefinitions(defs || []);
        setLocales(locs || []);
      })
      .catch((e) => toast.error(e.message));
  }, []);

  const myLocale = useMemo(
    () => locales.find((l) => l.current)?.code || null,
    [locales],
  );

  // ---- existing template -------------------------------------------------------------------

  const loadDetail = useCallback(() => {
    if (isNew) return Promise.resolve(null);
    return messageTemplateApi
      .get(numericId)
      .then((loaded) => {
        setDetail(loaded);
        return loaded;
      })
      .catch((e) => {
        toast.error(e.message);
        return null;
      });
  }, [isNew, numericId]);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    loadDetail().finally(() => setLoading(false));
  }, [isNew, loadDetail]);

  const variants = useMemo(() => detail?.variants || [], [detail]);
  const currentVariant = variants.find((v) =>
    sameLocale(v.localeId, routeLocale),
  );
  const mode = currentVariant ? "edit" : "add";

  // No language in the URL: open the admin's own, else en-US, else the first.
  useEffect(() => {
    if (isNew || !detail || routeLocale || !locales.length) return;
    const pick = pickDefaultLocale(variants, myLocale);
    if (pick) {
      navigate(templateUrl(detail.id, pick), {
        replace: true,
        state: keepState(location),
      });
    }
  }, [
    location,
    isNew,
    detail,
    routeLocale,
    locales.length,
    variants,
    myLocale,
    navigate,
  ]);

  // Canonical casing in the URL (the tabs compare exactly).
  useEffect(() => {
    if (currentVariant && currentVariant.localeId !== routeLocale) {
      navigate(templateUrl(detail.id, currentVariant.localeId), {
        replace: true,
        state: keepState(location),
      });
    }
  }, [location, currentVariant, routeLocale, detail, navigate]);

  // Adding a language: load the language to start from.
  const fromLocale = search.get("from") || "";
  useEffect(() => {
    if (isNew || !detail || !routeLocale || currentVariant) return;
    if (!fromLocale) {
      setStartContent({ subject: "", htmlTemplate: "" });
      return;
    }
    messageTemplateApi
      .variant(detail.id, fromLocale)
      .then((v) =>
        setStartContent({
          subject: v.subject || "",
          htmlTemplate: v.htmlTemplate || "",
        }),
      )
      .catch((e) => toast.error(e.message));
  }, [isNew, detail, routeLocale, currentVariant, fromLocale]);

  // Template-wide writes leave the languages alone, so the open editor keeps its unsaved edits;
  // it only refreshes its history (via templateRevision) to show the new entry.
  const onTemplateChanged = (updated) => {
    if (updated) setDetail(updated);
    else loadDetail();
  };

  const openLocale = (code) => {
    if (sameLocale(code, routeLocale)) return;
    if (!confirmDiscard(dirty)) return;
    setDirty(false);
    go(templateUrl(detail.id, code));
  };

  const addVariant = async (content) => {
    const created = await messageTemplateApi.addVariant(detail.id, {
      ...content,
      localeId: routeLocale,
    });
    toast.success(`${created.localeId} added.`);
    await loadDetail();
    go(templateUrl(detail.id, created.localeId), { replace: true });
  };

  // ---- new template ------------------------------------------------------------------------

  const definition = useMemo(
    () =>
      definitions.find(
        (d) => d.templateName === (isNew ? templateName : detail?.templateName),
      ) || null,
    [definitions, isNew, templateName, detail],
  );

  useEffect(() => {
    if (isNew && !newLocale && locales.length) {
      setNewLocale(
        locales.find((l) => l.code === "en-US")?.code || locales[0].code,
      );
    }
  }, [isNew, newLocale, locales]);

  const startOptions = useMemo(() => {
    if (!isNew || !definition) return [];
    return (definition.templates || []).flatMap((t) =>
      (t.localeIds || []).map((loc) => ({
        value: `${t.id}|${loc}`,
        label: `${t.description || `#${t.id}`} — ${loc}${
          t.active ? "" : " (deleted)"
        }`,
        localeId: loc,
      })),
    );
  }, [isNew, definition]);

  useEffect(() => {
    if (!isNew) return;
    const preferred =
      startOptions.find((o) => sameLocale(o.localeId, newLocale)) ||
      startOptions.find((o) => o.localeId === "en-US") ||
      startOptions[0];
    setStartFrom(preferred ? preferred.value : "");
    // Pick a starting point when the name changes, not on every locale change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, startOptions]);

  useEffect(() => {
    if (!isNew) return;
    if (!startFrom) {
      setStartContent({ subject: "", htmlTemplate: "" });
      return;
    }
    const [sourceId, sourceLocale] = startFrom.split("|");
    messageTemplateApi
      .variant(sourceId, sourceLocale)
      .then((v) =>
        setStartContent({
          subject: v.subject || "",
          htmlTemplate: v.htmlTemplate || "",
        }),
      )
      .catch((e) => toast.error(e.message));
  }, [isNew, startFrom]);

  const activeSingle =
    isNew &&
    definition &&
    !definition.multipleTemplates &&
    (definition.templates || []).find((t) => t.active);

  let createBlocker = null;
  if (isNew) {
    if (!templateName) createBlocker = "Choose a template name.";
    else if (activeSingle)
      createBlocker = `${templateName} already has a template; add a language to it instead.`;
    else if (definition?.multipleTemplates && !description.trim())
      createBlocker = "A description is required for this template name.";
    else if (!newLocale) createBlocker = "Choose a language.";
  }

  const create = async (content) => {
    const created = await messageTemplateApi.create({
      templateName,
      description,
      variant: { ...content, localeId: newLocale },
    });
    toast.success("Template created.");
    go(templateUrl(created.id, newLocale), { replace: true });
  };

  // ---- render ------------------------------------------------------------------------------

  const leave = () => {
    if (!confirmDiscard(dirty)) return;
    navigate(resolveReturnTo(location, PATHS.MESSAGE_TEMPLATES));
  };

  if (loading) {
    return (
      <Box className={classes.progressWrap}>
        <CircularProgress aria-label="Loading template" />
      </Box>
    );
  }

  if (!isNew && !detail) {
    return (
      <Container maxWidth="md" className={classes.page}>
        <Alert severity="error">This template could not be loaded.</Alert>
        <Button className={classes.back} onClick={leave}>
          ← All message templates
        </Button>
      </Container>
    );
  }

  return (
    <Box className={classes.page}>
      <Container maxWidth="xl">
        <Button className={classes.back} onClick={leave}>
          ← All message templates
        </Button>

        {isNew ? (
          <Paper
            className={classes.panel}
            component="section"
            aria-label="New template"
          >
            <Typography component="h1" className={classes.title}>
              New message template
            </Typography>
            {definition?.summary && (
              <Typography variant="body2">{definition.summary}</Typography>
            )}
            <Box className={classes.row}>
              <TextField
                select
                required
                id="mt-new-template-name"
                label="Template"
                variant="outlined"
                className={classes.grow}
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              >
                {definitions.map((d) => (
                  <MenuItem key={d.templateName} value={d.templateName}>
                    {d.templateName}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                id="mt-new-description"
                label="Description"
                variant="outlined"
                className={classes.grow}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required={Boolean(definition?.multipleTemplates)}
                helperText={
                  definition?.multipleTemplates
                    ? "Required: shown when picking this template on a qualifying score."
                    : "Optional internal note."
                }
                inputProps={{ maxLength: 255 }}
              />
            </Box>
            <Box className={classes.row}>
              <TextField
                select
                required
                id="mt-new-locale"
                label="First language"
                variant="outlined"
                className={classes.grow}
                value={newLocale}
                onChange={(e) => setNewLocale(e.target.value)}
                helperText="Add more languages after creating it."
              >
                {locales.map((l) => (
                  <MenuItem key={l.code} value={l.code}>
                    {l.code} — {l.englishName || l.nativeName}
                    {l.enabled ? "" : " (not offered to users yet)"}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                id="mt-new-start-from"
                label="Start from"
                variant="outlined"
                className={classes.grow}
                value={startFrom}
                onChange={(e) => setStartFrom(e.target.value)}
                disabled={!startOptions.length}
                helperText={
                  startOptions.length
                    ? "Copies its subject and body."
                    : "No existing template of this name."
                }
              >
                <MenuItem value="">
                  <em>Empty</em>
                </MenuItem>
                {startOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            {activeSingle && (
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    className={classes.touch}
                    onClick={() => go(templateUrl(activeSingle.id))}
                  >
                    Open it
                  </Button>
                }
              >
                {templateName} can only have one active template. Open it and
                use “Add language” to translate it.
              </Alert>
            )}
          </Paper>
        ) : (
          <TemplateHeader
            detail={detail}
            definition={definition}
            onChanged={onTemplateChanged}
          />
        )}

        {!isNew && (
          <Box className={classes.languageBar}>
            <Tabs
              value={
                routeLocale ? currentVariant?.localeId || routeLocale : false
              }
              onChange={(_, code) => openLocale(code)}
              variant="scrollable"
              scrollButtons="auto"
              indicatorColor="primary"
              className={classes.tabs}
              aria-label="Languages"
            >
              {variants.map((v) => (
                <Tab
                  key={v.localeId}
                  value={v.localeId}
                  className={classes.tab}
                  label={
                    <span className={v.active ? undefined : classes.deletedTab}>
                      {v.localeId}
                    </span>
                  }
                  aria-label={`${v.localeId}${v.active ? "" : " (deleted)"}`}
                />
              ))}
              {routeLocale && !currentVariant && (
                <Tab
                  value={routeLocale}
                  className={classes.tab}
                  label={`${routeLocale} (new)`}
                />
              )}
            </Tabs>
            <Button
              variant="outlined"
              color="primary"
              className={classes.touch}
              onClick={() => setAdding(true)}
              disabled={!detail.active}
            >
              Add language
            </Button>
          </Box>
        )}

        {isNew && (
          <VariantEditor
            key={`new-${templateName}-${newLocale}`}
            mode="create"
            templateName={templateName}
            localeId={newLocale}
            definition={definition}
            initialContent={startContent}
            onSubmit={create}
            submitLabel="Create template"
            submitBlocker={createBlocker}
            onDirtyChange={setDirty}
          />
        )}

        {!isNew && routeLocale && mode === "edit" && (
          <VariantEditor
            key={`edit-${detail.id}-${currentVariant.localeId}`}
            mode="edit"
            templateId={detail.id}
            templateName={detail.templateName}
            templateActive={detail.active}
            localeId={currentVariant.localeId}
            definition={definition}
            templateRevision={detail.revision}
            onChanged={loadDetail}
            onDirtyChange={setDirty}
          />
        )}

        {!isNew && routeLocale && mode === "add" && (
          <>
            <Alert severity="info" style={{ marginBottom: 16 }}>
              New {routeLocale} version
              {fromLocale ? `, starting from ${fromLocale}` : ""}. Nothing is
              saved until you add it.
            </Alert>
            <VariantEditor
              key={`add-${detail.id}-${routeLocale}`}
              mode="add"
              templateId={detail.id}
              templateName={detail.templateName}
              templateActive={detail.active}
              localeId={routeLocale}
              definition={definition}
              initialContent={startContent}
              onSubmit={addVariant}
              submitLabel={`Add ${routeLocale}`}
              submitBlocker={
                detail.active ? null : "Reactivate the template first."
              }
              onDirtyChange={setDirty}
            />
          </>
        )}

        {!isNew && (
          <AddLanguageDialog
            open={adding}
            locales={locales}
            variants={variants}
            defaultFrom={currentVariant?.localeId || "en-US"}
            onCancel={() => setAdding(false)}
            onConfirm={({ localeId: code, from }) => {
              setAdding(false);
              if (!confirmDiscard(dirty)) return;
              setDirty(false);
              go(templateUrl(detail.id, code, from ? { from } : undefined));
            }}
          />
        )}
      </Container>
    </Box>
  );
};

export default MessageTemplateEdit;
